import type {WithTranslations} from '@alchemy/react-form';
import {Integration} from './components/Integration/types.ts';
import {AssetAnnotation} from './components/Media/Asset/Annotations/annotationTypes.ts';
import {RenditionBuildMode} from './api/rendition.ts';
import {AttributeType} from './api/types.ts';
import {SortBy} from './components/Media/Search/Filter';
import {AQLQueries} from './components/Media/Search/AQL/query.ts';
import {ApiHydraObjectResponse} from '@alchemy/api';
import {Editor} from '@tiptap/core';
import {AclExtraPermission} from './components/Permissions/permissionsTypes.ts';
import {Privacy} from './api/privacy.ts';
import {DefinitionBase} from './components/Dialog/Workspace/DefinitionManager/managerTypes.ts';
import {UserPreferences} from './store/userPreferencesStore.ts';
import {BuiltInAttributeEnum} from './components/Media/Search/search.ts';
import {AttributeWidgetOptions} from './components/Media/Asset/Attribute/types/types';
import {
    FileAnalysis,
    FileAnalysisState,
} from './components/Media/Asset/Quarantine/analysisTypes.ts';

export type AlternateUrl = {
    type: string;
    url: string;
    label?: string;
};

export type FileUsageType = 'source' | 'version' | 'rendition';

export type FileUsage = {
    type: FileUsageType;
    assetId: string;
    assetTitle?: string | null;
    name?: string | null;
};

export interface ApiFile extends Entity {
    url?: string;
    type: string;
    extension: string;
    alternateUrls: AlternateUrl[];
    size: number;
    docUniqueId: string;
    checksum: string;
    fileName: string;
    metadata?: Record<string, any>;
    accepted?: boolean;
    analyzedAt?: string | null;
    // Only present on file resources and on rejected files
    analysis?: FileAnalysis | null | undefined;
    analysisPending: boolean;
    analysisState: FileAnalysisState;
    analysisEnforced: boolean;
    usages?: FileUsage[];
}

export type GroupValue = {
    name: string;
    key: string | null;
    values: any[];
    type: AttributeType;
};

export type User = {
    username: string;
    removed?: boolean;
} & Entity;

export type ShareAlternateUrl = {
    name: string;
    url: string;
    type: string | undefined;
    assetId?: string | null;
};

export type ShareAttachment = {
    id: string;
    name: string | null;
    assetId: string;
    url: string;
    type: string | null;
    size: number | null;
};

export type ShareTerms = {
    text: string | null;
    version: number;
    workspaceName: string;
    pdfUrl?: string | null;
};

export type Share = {
    name?: string | undefined;
    assets: Asset[];
    token: string;
    startsAt?: string | undefined | null;
    expiresAt?: string | undefined | null;
    updatedAt: Readonly<string>;
    createdAt: Readonly<string>;
    alternateUrls: ShareAlternateUrl[];
    attachments?: ShareAttachment[];
    terms?: ShareTerms | null;
    logo?: string | null;
} & Entity;

export type WorkspaceTerms = {
    // Resolved for the current user's locale
    text: string | null;
    // Untranslated source text (for editing)
    rawText?: string | null;
    translations?: Record<string, string> | null;
    version: number | null;
    signed: boolean | null;
    attachToExports: boolean;
    pdfUrl?: string | null;
};

export type ESDocumentState = {
    synced: boolean;
    data: object;
};

export type MatomoMediaMetrics = {
    nb_visits: number;
    nb_impressions: number;
    nb_interactions: number;
    sum_daily_nb_uniq_visitors: number;
    interaction_rate: string;
    nb_plays: number;
    nb_unique_visitors_plays: number;
    nb_unique_visitors_impressions: number;
    nb_finishes: number;
    sum_time_progress: number;
    nb_plays_with_tip: number;
    nb_plays_with_ml: number;
    sum_fullscreen_plays: number;
    play_rate: string;
    finish_rate: string;
    fullscreen_rate: string;
    avg_time_watched: string;
    avg_completion_rate: string;
    avg_media_length: string;
};

export type AssetAttachment = {
    asset?: Asset;
    attachment: Asset;
    name?: string | undefined;
    priority: number;
    updatedAt: Readonly<string>;
    createdAt: Readonly<string>;
} & Entity;

export interface Asset
    extends
        IPermissions<{
            edit: boolean;
            editAttributes: boolean;
            share: boolean;
            delete: boolean;
        }>,
        Entity {
    name?: string | undefined;
    nameHighlight: string | null;
    description?: string;
    privacy: number;
    tags: Tag[] | undefined;
    owner?: User;
    threadKey: string;
    thread?: Thread | undefined;
    workspace: Workspace;
    attributes: Attribute[];
    attachments: AssetAttachment[];
    referenceCollection?: Collection | undefined;
    collections: Collection[] | undefined;
    main: AssetRendition | null;
    preview: AssetRendition | null;
    source: ApiFile | undefined;
    thumbnail: AssetRendition | null;
    animatedThumbnail: AssetRendition | null;
    createdAt: string;
    updatedAt: string;
    editedAt: string;
    attributesEditedAt: string;
    groupValue?: GroupValue | undefined;
    topicSubscriptions?: TopicSubscriptions;
    storyCollection?: Collection | undefined;
    deleted?: boolean;
    trackingId?: string;
    status: AssetStatus;
    resolvedTrackingId: string;
}

export enum AssetStatus {
    Accepted = 0,
    Pending = 1,
    Quarantined = 2,
}

type AttrValue = any;

type AttributeOrigin = 'human' | 'machine' | 'fallback' | 'initial';

export interface Attribute extends IPermissions, Entity {
    definition: AttributeDefinition;
    origin: AttributeOrigin;
    multiple: boolean;
    invalid?: boolean;
    originVendor?: string;
    locale?: string | undefined;
    originUserId?: string;
    originVendorContext?: string;
    value: AttrValue;
    highlight: AttrValue;
    assetAnnotations?: AssetAnnotation[];
}

export interface AssetFileVersion extends Entity {
    asset: Asset;
    file: ApiFile;
    name: string;
    createdAt: string;
}

export interface BaseAttributeDefinition extends Entity {
    name: string;
    enabled: boolean;
    displayName: string;
    type: AttributeType;
    searchable: boolean;
    sortable: boolean;
    entityList?: EntityList | string | null | undefined;
    multiple: boolean;
    facetEnabled: boolean;
    entityIri?: string;
    slug: string;
    searchSlug: string;
    widgetOptions?: AttributeWidgetOptions;
    builtIn?: true;
}

export interface BuiltInAttribute extends BaseAttributeDefinition {
    builtIn: true;
}

export interface AttributeDefinition
    extends BaseAttributeDefinition, IPermissions {
    builtIn?: never;
    editable: boolean;
    editableInGui: boolean;
    suggest: boolean;
    translatable: boolean;
    locales?: string[];
    allowInvalid: boolean;
    fillFromName: boolean;
    namePriority?: number | null;
    canEdit: boolean;
    searchBoost: number;
    fallback: Record<string, string>;
    initialValues: Record<string, string>;
    readFromMetadata?: string[];
    writeMetadata?: string[];
    writeMetadataRenditions?: string[];
    workspace: Workspace | string;
    policy: AttributePolicy | string | null;
    lastErrors?: LastErrors;
    target: AssetType;
}

export type AttributeDefinitionOrBuiltIn =
    | AttributeDefinition
    | BuiltInAttribute;

export interface AttributePolicy extends ApiHydraObjectResponse, Entity {
    name: string;
    public: boolean;
    editable: boolean;
    workspace: Workspace | string;
}

export interface RenditionPolicy extends ApiHydraObjectResponse, Entity {
    name: string;
    public: boolean;
    editable: boolean;
    workspace: Workspace | string;
}

export enum AssetPolicyConditionOperator {
    Equals = '=',
}

export enum AssetPolicyActionName {
    HideRendition = 'hide_rendition',
    HideAttribute = 'hide_attribute',
}

export type AssetPolicyCondition = {
    field?: string;
    operator: string;
    value: AssetPolicyCondition[] | string | number | boolean;
};

export type AssetPolicyAction = {
    action: AssetPolicyActionName;
} & Record<string, any>;

export interface AssetPolicy extends ApiHydraObjectResponse, Entity {
    name: string;
    enabled: boolean;
    workspace: Workspace | string;
    conditions: AssetPolicyCondition[];
    actions: AssetPolicyAction[];
    owner: User;
    users: User[] | string[];
    groups: Group[] | string[];
}

export interface FieldType extends ApiHydraObjectResponse {
    name: string;
    displayName: string;
}

export interface RenditionDefinition extends ApiHydraObjectResponse, Entity {
    name: string;
    displayName: string;
    parent?: RenditionDefinition | string | undefined | null;
    policy: RenditionPolicy | string | null;
    workspace: Workspace | string;
    definition: string;
    buildMode?: RenditionBuildMode | string;
    substitutable: boolean;
    writeMetadata: boolean;
    metadata?: Record<string, string> | null;
    useAsMain?: boolean;
    useAsPreview?: boolean;
    useAsThumbnail?: boolean;
    useAsAnimatedThumbnail?: boolean;
    priority: number;
    target: AssetType;
}

export interface AssetRendition extends ApiHydraObjectResponse, Entity {
    name: string;
    displayName: string;
    file: ApiFile | undefined;
    ready: boolean;
    dirty?: boolean;
    projection?: boolean;
    locked: boolean;
    substituted: boolean;
    definition: Pick<RenditionDefinition, 'id' | 'substitutable'> | null;
}

export interface RenditionPolicy extends ApiHydraObjectResponse, Entity {
    name: string;
    workspace: Workspace | string;
    public: boolean;
}

export interface RenditionRule extends ApiHydraObjectResponse, Entity {
    name: string;
    userId: string | null;
    groupId: string | null;
    workspaceId: string | null;
    collectionId: string | null;
    group: Group | null;
    user: User | null;
    allowed: RenditionPolicy[];
}

export type TPermission<E extends Record<string, boolean> = {}> = {
    edit: boolean;
    delete: boolean;
    editPermissions: boolean;
} & E;

export interface IPermissions<
    E extends Record<string, boolean> = {},
> extends ApiHydraObjectResponse {
    capabilities: TPermission<E>;
}

export interface AttributeFilterRule extends ApiHydraObjectResponse, Entity {
    users: {id: string; name: string}[];
    groups: {id: string; name: string}[];
    workspaceId?: string;
    condition: string;
}

export type KeyTranslations = {
    [locale: string]: string;
};

type EntitySynonyms = {
    [locale: string]: string[];
};

export enum AttributeEntityStatus {
    Approved = 0,
    Pending = 1,
    Rejected = 2,
}

export type AttributeEntity = {
    type: string;
    locale: string;
    value: string;
    emoji?: string;
    color?: string;
    translations: KeyTranslations;
    synonyms?: EntitySynonyms;
    createdAt: string;
    updatedAt: string;
    status: AttributeEntityStatus;
} & ApiHydraObjectResponse &
    Entity;

export type EntityList = {
    name: string;
    definitions: AttributeDefinition[];
    allowNewValues?: boolean;
    approveNewValues?: boolean;
    withTranslations?: boolean;
    withSynonyms?: boolean;
    withEmojis?: boolean;
    withColors?: boolean;
    createdAt: string;
    updatedAt: string;
} & ApiHydraObjectResponse &
    Entity;

export interface Tag extends ApiHydraObjectResponse, WithTranslations, Entity {
    name: string;
    displayName: string;
    color: string | null;
    workspace: Workspace | string;
}

export interface Group extends Entity {
    name: string;
}

export type CollectionOptionalWorkspace = {workspace?: Workspace} & Omit<
    Collection,
    'workspace'
>;

export type CollectionPrivacyInfo = {
    privacy: Privacy;
    computedPrivacy: Privacy;
    canEditAssetPrivacy: boolean;
};

export interface Collection
    extends
        IPermissions<{
            createAsset: boolean;
            createCollection: boolean;
            edit: boolean;
            delete: boolean;
        }>,
        Entity,
        ApiHydraObjectResponse {
    parentId?: string;
    parent?: Collection;
    name: string;
    displayName: string;
    nameHighlight?: string;
    storyAsset?: Asset;
    absoluteName?: string;
    absoluteDisplayName?: string;
    children?: CollectionOptionalWorkspace[];
    workspace: Workspace;
    public: boolean;
    shared: boolean;
    privacy: number;
    inheritedPrivacy?: number;
    createdAt: string;
    updatedAt: string;
    owner?: User;
    topicSubscriptions?: TopicSubscriptions;
    deleted?: boolean;
}

export interface Basket
    extends
        IPermissions<{
            edit: boolean;
            share: boolean;
            delete: boolean;
            editPermissions: boolean;
        }>,
        Entity {
    name: string;
    nameHighlight?: string | undefined;
    description?: string | undefined;
    descriptionHighlight?: string | undefined;
    assetCount?: number;
    createdAt: string;
    updatedAt: string;
    isArchived: boolean;
    owner?: User;
}

export enum ProfileItemSection {
    Attributes = 0,
    Facets = 1,
    Grid = 2,
}

export enum ProfileItemType {
    Definition = 0,
    BuiltIn = 1,
    Divider = 2,
    Spacer = 3,
}

export type GridRegion = 'over' | 'below';

// above/below bands: l | c | r ; over (3x3 grid): tc..bc — the top-left and
// top-right cells are reserved for the item controls (checkbox, menu button)
// and the bottom-right one for the file type chip.
export type GridAnchor =
    | 'l'
    | 'c'
    | 'r'
    | 'tc'
    | 'ml'
    | 'cc'
    | 'mr'
    | 'bl'
    | 'bc';

export type ItemPlacement = {
    region: GridRegion;
    anchor: GridAnchor;
    order?: number;
};

// How a grid value is rendered: its rich ReactNode, a plain-text chip, or raw
// text. The rich rendering may be tuned through the item's format
// (AttributeFormat, e.g. Privacy's "short" icon-only format).
export enum ProfileItemVariant {
    Rich = 'rich',
    Chip = 'chip',
    Text = 'text',
}

// Rendered size of a grid value (any variant).
export enum ProfileItemSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
}

export type ProfileItem = {
    id: string;
    section: ProfileItemSection;
    type: ProfileItemType;
    key?: BuiltInAttributeEnum | string;
    definition?: string;
    displayEmpty?: boolean;
    format?: string;
    // Grid section only:
    placement?: ItemPlacement;
    variant?: ProfileItemVariant;
    // One of the 12 theme chip colors (theme.palette.chips); empty for default.
    color?: string;
    size?: ProfileItemSize;
    showLabel?: boolean;
    showIcon?: boolean;
};

export interface DisplayProfile
    extends
        IPermissions<{
            edit: boolean;
            delete: boolean;
            editPermissions: boolean;
        }>,
        Entity {
    name: string;
    description?: string;
    items?: ProfileItem[];
    exclusive?: boolean; // if true, only items in this list well be shown otherwise all attributes
    public?: boolean;
    createdAt: string;
    data?: UserPreferences;
    updatedAt: string;
    owner?: User;
}

export type SavedSearchData = {
    query?: string;
    conditions: AQLQueries;
    sortBy: SortBy[];
};

export enum SavedSearchPrivacy {
    Secret = 0,
    Private = 1,
    Public = 2,
}

export interface SavedSearch
    extends
        IPermissions<{
            edit: boolean;
            delete: boolean;
            editPermissions: boolean;
        }>,
        Entity {
    name: string;
    privacy?: SavedSearchPrivacy;
    createdAt: string;
    updatedAt: string;
    data: SavedSearchData;
    owner?: User;
}

export interface Page extends IPermissions, Entity {
    title: string;
    description?: string;
    slug: string;
    enabled?: boolean;
    public?: boolean;
    createdAt: string;
    updatedAt: string;
    data?: PageContent;
    owner?: User;
}

export type PageContent = ReturnType<Editor['getJSON']>;

export interface Thread extends Entity {
    id: string;
    key: string;
    createdAt: string;
}

export type MessageAttachment = {
    type: string;
    content: string;
};

export type DeserializedMessageAttachment = {
    id?: string;
    type: string;
    data: Record<string, any>;
};

export interface ThreadMessage extends Entity {
    id: string;
    content: string;
    attachments?: MessageAttachment[];
    author: User;
    createdAt: string;
    updatedAt: string;
    acknowledged?: boolean;
    capabilities: {
        delete: boolean;
        edit: boolean;
    };
}

export interface BasketAsset extends Entity {
    asset: Asset;
    context?: any;
    nameHighlight: string;
    position: number;
    createdAt: string;
    owner?: User;
    assetAnnotations?: AssetAnnotation[];
}

export type LastErrors = {
    date: string;
    message: string;
    code: number;
    file: string;
    line: number;
}[];

export interface Workspace
    extends
        IPermissions<{
            createCollection: boolean;
            createAsset: boolean;
            edit: boolean;
        }>,
        Entity {
    name: string;
    displayName: string;
    fileAnalyzers?: string;
    trashRetentionDelay?: number;
    assetDefaultStatus?: AssetStatus;
    fileAnalysisRequired?: boolean;
    enabledLocales?: string[] | undefined;
    localeFallbacks?: string[] | undefined;
    owner?: User;
    createdAt: string;
    public: boolean;
    terms?: WorkspaceTerms | null;
    termsUnsigned?: boolean;
    logo?: string | null;
    // Form-only fields (mapped to the API on submit)
    termsText?: string;
    // undefined = untouched, '' = remove, File = multipart upload on submit
    termsPdf?: File | '';
    logoUpload?: File | '';
    attachTermsToExports?: boolean;
}

export type IntegrationData = {
    object?: object | undefined;
    keyId: string | null;
    name: string;
    value: any;
} & Entity;

export type IntegrationConfigKey = {
    label: string;
    description: string;
    value: string | undefined;
};

export interface WorkspaceIntegration
    extends
        DefinitionBase,
        IPermissions<{
            use: boolean;
            interact: boolean;
        }> {
    name: string;
    public: boolean;
    enabled: boolean;
    integration: Integration;
    integrationName: string;
    data: IntegrationData[];
    config: object;
    configYaml: string;
    configInfo?: IntegrationConfigKey[];
    tokens: IntegrationToken[];
    workspace: Workspace | string;
    owner?: User;
    if?: string;
    needs?: string[];
    lastErrors?: LastErrors;
}

export interface ReferenceSection {
    name: string;
    description?: string | null;
    reference: string;
}

export interface IntegrationType {
    id: string;
    displayName: string;
    name: string;
    reference: string;
    references: ReferenceSection[];
}

export interface RenditionBuildReference {
    id: string;
    reference: string;
    references: ReferenceSection[];
}

export type IntegrationToken = {
    userId: string;
    expired: boolean;
    expiresAt: string;
    createdAt: string;
} & Entity;

export enum UserType {
    User = 'user',
    Group = 'group',
}

export enum CollectionOrWorkspace {
    Collection = 'collection',
    Workspace = 'workspace',
}

export enum AssetType {
    Asset = 1,
    Story = 2,
    Both = 3,
}

export enum AssetTypeFilter {
    All = 0,
    Asset = 1,
    Story = 2,
}

export type Ace = (
    | {
          userType: UserType.Group;
          group?: Group | null;
      }
    | {
          userType: UserType.User;
          user?: User | null;
      }
) & {
    mask: number;
    userId: string | null;
    userType: UserType;
    resolving?: boolean;
    wildcard?: boolean;
    metadata?: AclExtraPermission[];
} & Entity;

export type StateSetter<T> = (handler: T | ((prev: T) => T)) => void;
export type StateSetterHandler<T> = (handler: (prev: T) => T) => void;

export type AssetOrAssetContainer = {} & Entity;

export interface Entity {
    id: string;
}

// List of events the current user is subscribed to for the object
export type TopicSubscriptions<T extends string = string> = T[];

export enum ExportStatusEnum {
    Pending = 0,
    InProgress = 1,
    Ready = 2,
    Failed = 3,
}

export type AssetExport = {
    id: string;
    status: ExportStatusEnum;
    error?: string;
    progress?: number;
    downloadUrl?: string;
    unregister?: () => void;
};
